import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useAddresses from '../../hooks/useAddresses';
import PageHeader from '../../components/ui/PageHeader';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import AddressCard from '../../components/AddressCard';
import AddressFormModal from '../../components/AddressFormModal';
import DateTimeSlotPicker from '../../components/DateTimeSlotPicker';
import CountSelector from '../../components/CountSelector';

export default function ModifyOrder() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [listing, setListing] = useState(null);
  const [message, setMessage] = useState('');
  const [answers, setAnswers] = useState({});
  const [scheduledAt, setScheduledAt] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initError, setInitError] = useState(false);

  const questions = (listing?.listing_questions || []).filter(lq => lq.question.type !== 'datetime');

  const setAnswer = (questionId, value) => {
    setAnswers(a => ({ ...a, [questionId]: value }));
  };

  const {
    addresses,
    loading: addressesLoading,
    createAddress,
    updateAddress,
  } = useAddresses();
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  // Fetch the order, guard access, then fetch its listing for the question definitions.
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_API_URL}/api/orders/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        const fetchedOrder = res.data.order;
        if (user?.id !== fetchedOrder.customer_id || !['pending', 'accepted'].includes(fetchedOrder.status)) {
          navigate(`/orders/${id}`);
          return;
        }
        setOrder(fetchedOrder);
        setMessage(fetchedOrder.message || '');
        setScheduledAt(fetchedOrder.scheduled_at || '');

        const prefilledAnswers = {};
        for (const a of fetchedOrder.order_answers || []) {
          if (!a.question_id) continue;
          if (a.question_type === 'boolean') prefilledAnswers[a.question_id] = a.answer_boolean;
          else if (a.question_type === 'number') prefilledAnswers[a.question_id] = a.answer_number;
          else prefilledAnswers[a.question_id] = a.answer_text;
        }
        setAnswers(prefilledAnswers);

        return axios.get(`${process.env.REACT_APP_API_URL}/api/listings/${fetchedOrder.listing_id}`);
      })
      .then(res => { if (res) setListing(res.data.listing); })
      .catch(() => setInitError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Best-effort match to a saved address; otherwise the customer must pick/add one.
  useEffect(() => {
    if (!order || selectedAddressId || addresses.length === 0) return;
    const match = addresses.find(a =>
      a.address_line === order.address_line && a.building === order.building && a.unit === order.unit
    );
    if (match) setSelectedAddressId(match.id);
  }, [order, addresses, selectedAddressId]);

  const handleAddressSaved = (address) => {
    setSelectedAddressId(address.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const selectedAddress = addresses.find(a => a.id === selectedAddressId);
    if (!selectedAddress) {
      setError('Please choose or add a delivery address.');
      return;
    }

    if (!scheduledAt) {
      setError('Please choose a service date and time.');
      return;
    }

    for (const lq of questions) {
      const value = answers[lq.question.id];
      if (lq.required && (value === undefined || value === null || value === '')) {
        setError(`Please answer: ${lq.question.label}`);
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/orders/${id}/modify`,
        {
          message,
          address_line: selectedAddress.address_line,
          building: selectedAddress.building,
          unit: selectedAddress.unit,
          latitude: selectedAddress.latitude,
          longitude: selectedAddress.longitude,
          scheduled_at: scheduledAt,
          answers: Object.entries(answers)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([question_id, value]) => ({ question_id, value })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/orders/${id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (initError) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
      <p className="text-sm text-ink-400">This request could not be loaded, or can no longer be modified.</p>
      <Button variant="secondary" onClick={() => navigate(`/orders/${id}`)}>Back to request</Button>
    </div>
  );

  if (!order || !listing) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div role="status" aria-label="Loading" className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="bg-sand-50 min-h-screen pb-24 sm:pb-0">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <PageHeader title="Modify Request" subtitle="Update your details — the provider will need to re-accept" />

        {/* Listing preview */}
        <div className="bg-white border border-sand-200 rounded-lg p-4 flex gap-4 items-start mb-8 shadow-card">
          <img
            src={listing.cover_photo_url || listing.cover_photo}
            alt={listing.title}
            className="w-16 h-16 rounded-lg object-cover shrink-0 bg-sand-100"
          />
          <div>
            <p className="font-heading text-sm font-semibold text-ink-900">{listing.title}</p>
            <p className="text-xs text-ink-400 mt-0.5">{listing.category}</p>
            <p className="text-sm font-semibold text-terra-400 mt-1">AED {listing.price}</p>
          </div>
        </div>

        <form id="modify-order-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Delivery Address */}
          <div>
            <label className="text-sm font-medium text-ink-700 block mb-2">
              Delivery Address
            </label>

            {addressesLoading ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-6 h-6 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {addresses.map(address => (
                  <AddressCard
                    key={address.id}
                    address={address}
                    selectable
                    selected={selectedAddressId === address.id}
                    onSelect={() => setSelectedAddressId(address.id)}
                  />
                ))}

                <button
                  type="button"
                  onClick={() => setAddressModalOpen(true)}
                  className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-sand-300 text-sm font-medium text-ink-700 hover:border-terra-400 hover:text-terra-400 transition-all duration-200 py-3"
                >
                  <Plus className="w-4 h-4" /> Add new address
                </button>
              </div>
            )}
          </div>

          <DateTimeSlotPicker onChange={setScheduledAt} required initialValue={order.scheduled_at} />

          {questions.map(lq => {
            const q = lq.question;
            const value = answers[q.id];
            const label = `${q.label}${lq.required ? '' : ' (optional)'}`;

            if (q.type === 'number') {
              return (
                <CountSelector
                  key={q.id}
                  label={q.unit ? `${q.label} — ${q.unit}` : q.label}
                  unit={q.unit}
                  value={value === undefined ? null : value}
                  onChange={v => setAnswer(q.id, v)}
                  required={lq.required}
                />
              );
            }

            if (q.type === 'boolean') {
              return (
                <div key={q.id}>
                  <label className="text-sm font-medium text-ink-700 block mb-1">{label}</label>
                  <select
                    value={value === undefined ? '' : String(value)}
                    onChange={e => setAnswer(q.id, e.target.value === '' ? undefined : e.target.value === 'true')}
                    required={lq.required}
                    className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
                  >
                    <option value="">Select…</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              );
            }

            return (
              <Input
                key={q.id}
                id={`q-${q.id}`}
                label={label}
                as="textarea"
                rows={2}
                value={value ?? ''}
                onChange={e => setAnswer(q.id, e.target.value)}
                required={lq.required}
              />
            );
          })}

          <Input
            id="message"
            label="Message (optional)"
            as="textarea"
            rows={4}
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Any details for the provider…"
          />

          {error && <p className="text-sm text-uae-red">{error}</p>}

          {/* Desktop CTA */}
          <div className="hidden sm:flex gap-3">
            <Button type="button" variant="secondary" onClick={() => navigate(`/orders/${id}`)} className="w-full justify-center">
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={loading} className="w-full justify-center">
              Save Changes
            </Button>
          </div>
        </form>
      </div>

      {/* Mobile fixed CTA */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-sand-200 p-4 sm:hidden">
        <Button type="submit" form="modify-order-form" variant="primary" loading={loading} className="w-full justify-center">
          Save Changes
        </Button>
      </div>

      <AddressFormModal
        isOpen={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        address={null}
        forceDefault={addresses.length === 0}
        createAddress={createAddress}
        updateAddress={updateAddress}
        onSaved={handleAddressSaved}
      />
    </div>
  );
}
